import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AddAPhotoOutlined, Close } from "@mui/icons-material";
import PropertyPhotoGallery from "../PropertyPhotoGallery/PropertyPhotoGallery";
import type { GoogleSlide } from "../../../../Hooks/useGoogleSlides";
import { DASH } from "../../dashboard/tokens";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  googleSlides: GoogleSlide[];
  ownerPhotos: any[];
  mls?: any;
  propertyId: string;
  isClaimed: boolean;
  canManage: boolean;
  onManage: () => void;
};

/**
 * "View Photos" from the header: the existing slider, in a dialog. Full
 * screen on phones, where a centred dialog left the address truncated next to
 * the button.
 */
const PhotoGalleryDialog: React.FC<Props> = ({
  open,
  onClose,
  title,
  googleSlides,
  ownerPhotos,
  mls,
  propertyId,
  isClaimed,
  canManage,
  onManage,
}) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={phone}
      PaperProps={{ sx: { borderRadius: phone ? 0 : "18px" } }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: { xs: 4, sm: 6 }, pt: 4, pb: 2 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, color: DASH.teal, fontSize: 18, lineHeight: 1.3 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: DASH.muted }}>
            {ownerPhotos.length > 0
              ? `${ownerPhotos.length} owner photo${ownerPhotos.length === 1 ? "" : "s"} plus map views`
              : "Satellite and street views from Google"}
          </Typography>
          {canManage && (
            <Button
              onClick={onManage}
              startIcon={<AddAPhotoOutlined />}
              variant="outlined"
              size="small"
              sx={{ mt: 2, borderColor: DASH.teal, color: DASH.teal, fontWeight: 700, whiteSpace: "nowrap" }}
              data-testid="gallery-manage-photos"
            >
              Manage photos
            </Button>
          )}
        </Box>
        <IconButton aria-label="Close photos" onClick={onClose} sx={{ mt: -1 }}>
          <Close />
        </IconButton>
      </Stack>
      <DialogContent sx={{ px: { xs: 3, sm: 6 }, pb: 6, display: "flex", flexDirection: "column", justifyContent: { xs: "center", sm: "flex-start" } }}>
        <PropertyPhotoGallery
          googleSlides={googleSlides}
          ownerPhotos={ownerPhotos}
          mls={mls}
          propertyId={propertyId}
          isClaimed={isClaimed}
          canManage={canManage}
          onManage={onManage}
          hideManageButton
        />
      </DialogContent>
    </Dialog>
  );
};

export default PhotoGalleryDialog;
